
import React, { useState, useCallback, useEffect, useRef, FC } from 'react';
import { 
  User, 
  UserRole, 
  Product, 
  Order, 
  OrderStatus, 
  Invoice, 
  Testimonial, 
  Notification,
  NewProductData,
  NewOrderData,
  NewTestimonialData,
  NewCustomerData,
  NotificationType,
  Inquiry,
  NewInquiryData,
  SiteContent,
  Category,
  ThemeConfig,
  InventoryItem,
  InventoryUsageLog,
  OrderEvent,
  Shipment,
  PaymentTransaction,
  CustomerBranch,
  NewCustomerBranchData,
  ThemeMode,
  ResolvedTheme,
  CreateOrderData
} from '../types';
import { supabaseBackend, hasValidConfig, getClient, listMissingRequiredTables } from '../services/supabaseBackend';
import { BackendService } from '../services/backendInterface';
import { getOrderOwningCustomerId } from '../services/workflowUtils';
import { getInvoicePayableAmount } from '../services/invoiceUtils';
import { buildReceiptDocumentHtml, buildReceiptLabel, getLatestTransactionByInvoice } from '../services/paymentUtils';
import { AppContext, AppProviderProps } from './AppContextInstance';
import { isCustomerRole as isCustomerWorkflowRole, resolveOrganizationAdminId as resolveWorkflowOrganizationAdminId } from '../services/workflowUtils';

const hexToRgb = (hex: string) => {
    let c: any;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = '0x' + c.join('');
        return [(c >> 16) & 255, (c >> 8) & 255, c & 255];
    }
    return [0, 0, 0];
}

const adjustBrightness = (hex: string, percent: number) => {
    const [r, g, b] = hexToRgb(hex);
    const amt = Math.floor(2.55 * percent);
    const R = (r + amt < 255) ? (r + amt < 0 ? 0 : r + amt) : 255;
    const G = (g + amt < 255) ? (g + amt < 0 ? 0 : g + amt) : 255;
    const B = (b + amt < 255) ? (b + amt < 0 ? 0 : b + amt) : 255;
    return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
}

const loadRazorpayCheckoutScript = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  if ((window as any).Razorpay) return true;

  return new Promise((resolve) => {
    const existing = document.querySelector('script[data-razorpay-checkout="true"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(true), { once: true });
      existing.addEventListener('error', () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.setAttribute('data-razorpay-checkout', 'true');
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const getApiUrl = (path: string) => {
  const baseUrl = String(import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/$/, '');
  return baseUrl ? `${baseUrl}${path}` : path;
};

const THEME_STORAGE_KEY = 'agm_theme_mode';

type SerializedOrder = Omit<Order, 'createdAt' | 'approvedAt' | 'deniedAt' | 'shippedAt' | 'deliveredAt'> & {
  createdAt: string | Date;
  approvedAt?: string | Date;
  deniedAt?: string | Date;
  shippedAt?: string | Date;
  deliveredAt?: string | Date;
};

const resolveThemeMode = (mode: ThemeMode): ResolvedTheme => {
  if (typeof window === 'undefined') {
    return 'light';
  }
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
};

const hydrateOrderDates = (order: SerializedOrder): Order => ({
  ...order,
  createdAt: new Date(order.createdAt),
  approvedAt: order.approvedAt ? new Date(order.approvedAt) : undefined,
  deniedAt: order.deniedAt ? new Date(order.deniedAt) : undefined,
  shippedAt: order.shippedAt ? new Date(order.shippedAt) : undefined,
  deliveredAt: order.deliveredAt ? new Date(order.deliveredAt) : undefined,
});

const waitForRefreshToFinish = async (isRefreshing: () => boolean) => {
  for (let attempts = 0; attempts < 50 && isRefreshing(); attempts += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
};

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<User[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]); 
  const [customerInbox, setCustomerInbox] = useState<Notification[]>([]);
  const [adminInbox, setAdminInbox] = useState<Notification[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [siteContent, setSiteContent] = useState<Record<string, any>>({});
  const [orderEvents, setOrderEvents] = useState<OrderEvent[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [inventoryUsageLogs, setInventoryUsageLogs] = useState<InventoryUsageLog[]>([]);
  const [branches, setBranches] = useState<CustomerBranch[]>([]);
  const [paymentTransactions, setPaymentTransactions] = useState<PaymentTransaction[]>([]);
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveThemeMode(typeof window === 'undefined' ? 'system' : ((window.localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode) || 'system')));
  const [setupRequired, setSetupRequired] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const refreshInFlightRef = useRef(false);
  const unreadSystemNotificationKeysRef = useRef<Set<string>>(new Set());

  const backend: BackendService = supabaseBackend;
  const adminRoles = [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.MANAGER, UserRole.ACCOUNTANT];
  const isCustomerRole = useCallback((role?: UserRole | null) => isCustomerWorkflowRole(role), []);
  const resolveOrganizationAdminId = useCallback((user: User | null): number | string | null => {
    return resolveWorkflowOrganizationAdminId(user);
  }, []);

  useEffect(() => {
    const applyTheme = (mode: ThemeMode) => {
      const nextResolved = resolveThemeMode(mode);
      setResolvedTheme(nextResolved);
      document.documentElement.classList.toggle('dark', nextResolved === 'dark');
      document.documentElement.style.colorScheme = nextResolved;
      document.documentElement.dataset.theme = nextResolved;
    };

    applyTheme(themeMode);
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);

    if (themeMode !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleMediaChange = () => applyTheme('system');
    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, [themeMode]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
  }, []);

  const cycleThemeMode = useCallback(() => {
    setThemeModeState((current) => (current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light'));
  }, []);

  const refreshFinancialData = useCallback(async () => {
    const [refreshedInvoices, refreshedOrders, refreshedTransactions] = await Promise.all([
      backend.invoices.list(),
      backend.orders.list(),
      backend.payments?.listTransactions ? backend.payments.listTransactions() : Promise.resolve([]),
    ]);
    setInvoices(refreshedInvoices);
    setOrders(refreshedOrders);
    setPaymentTransactions(refreshedTransactions || []);
  }, [backend.invoices, backend.orders, backend.payments]);

  const getAuthHeaders = useCallback(async (includeJson = true) => {
    const { data: sessionData } = await getClient().auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) {
      throw new Error('Your session has expired. Please log in again.');
    }
    return {
      ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${accessToken}`
    } as Record<string, string>;
  }, []);

  const callAuthorizedApi = useCallback(async (path: string, init?: RequestInit) => {
    const headers = await getAuthHeaders(!(init?.body instanceof FormData));
    const response = await fetch(getApiUrl(path), {
      ...init,
      headers: {
        ...headers,
        ...(init?.headers || {})
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(payload?.message || `Request failed for ${path}`);
    }
    return payload;
  }, [getAuthHeaders]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setCurrentUser(null);
    setIsAuthenticated(false);
    setCustomerInbox([]);
    setAdminInbox([]);
    unreadSystemNotificationKeysRef.current = new Set();
    localStorage.removeItem('agm_user');
    try { 
        await backend.auth.logout(); 
    } catch (e) { 
        console.warn("Backend logout failed:", e); 
    } finally { 
        window.location.reload(); 
    }
  }, [backend.auth]);

  // Auth State Listener to handle session expiration reactively
  useEffect(() => {
    if (!hasValidConfig()) return;
    
    try {
        const client = getClient();
        const { data: { subscription } } = client.auth.onAuthStateChange((event: string, session: any) => {
            if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
                if (localStorage.getItem('agm_user')) {
                    console.log("Auth session changed:", event, "Clearing local state.");
                  localStorage.removeItem('agm_user');
                  setCurrentUser(null);
                  setIsAuthenticated(false);
                  setCustomerInbox([]);
                  setAdminInbox([]);
                }
            }
        });
        return () => subscription.unsubscribe();
    } catch (e) {
        console.warn("Auth listener setup failed (likely due to missing tables):", e);
    }
  }, []);

  useEffect(() => {
    if (siteContent.theme_config) {
        const theme: ThemeConfig = siteContent.theme_config;
        const root = document.documentElement;
        if (theme.fontFamily && theme.fontFamily !== 'Inter') {
            const fontName = theme.fontFamily.replace(/ /g, '+');
            const linkId = 'dynamic-font-loader';
            let link = document.getElementById(linkId) as HTMLLinkElement;
            if (!link) {
                link = document.createElement('link');
                link.id = linkId;
                link.rel = 'stylesheet';
                document.head.appendChild(link);
            }
            link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@300;400;500;600;700&display=swap`;
            root.style.setProperty('font-family', `"${theme.fontFamily}", sans-serif`);
        } else if (theme.fontFamily === 'Inter') {
            root.style.removeProperty('font-family');
        }
        if (theme.primaryColor) {
            const p = theme.primaryColor;
            root.style.setProperty('--color-primary-50', adjustBrightness(p, 180));
            root.style.setProperty('--color-primary-100', adjustBrightness(p, 150));
            root.style.setProperty('--color-primary-200', adjustBrightness(p, 120));
            root.style.setProperty('--color-primary-300', adjustBrightness(p, 80));
            root.style.setProperty('--color-primary-400', adjustBrightness(p, 40));
            root.style.setProperty('--color-primary-500', p);
            root.style.setProperty('--color-primary-600', adjustBrightness(p, -10));
            root.style.setProperty('--color-primary-700', adjustBrightness(p, -30));
            root.style.setProperty('--color-primary-800', adjustBrightness(p, -50));
            root.style.setProperty('--color-primary-900', adjustBrightness(p, -70));
        }
        if (theme.borderRadius) {
            const remMap: Record<string, string> = { 'none': '0', 'sm': '0.125rem', 'md': '0.375rem', 'lg': '0.5rem', 'xl': '0.75rem', '2xl': '1rem' };
            const r = remMap[theme.borderRadius] || '0.375rem';
            root.style.setProperty('--radius-md', r);
            root.style.setProperty('--radius-lg', `calc(${r} + 0.125rem)`);
            root.style.setProperty('--radius-xl', `calc(${r} + 0.375rem)`);
            root.style.setProperty('--radius-2xl', `calc(${r} + 0.625rem)`);
        }
    }
  }, [siteContent]);

  const addNotification = useCallback((message: string, type: NotificationType) => {
    const now = new Date();
    const collapseWindowMs = type === 'success' ? 1800 : 1200;
    const maxVisibleNotifications = 4;

    setNotifications((prev) => {
      const lastMatchingIndex = [...prev]
        .reverse()
        .findIndex(
          (item) =>
            item.message === message &&
            item.type === type &&
            now.getTime() - item.createdAt.getTime() <= collapseWindowMs
        );

      if (lastMatchingIndex !== -1) {
        const actualIndex = prev.length - 1 - lastMatchingIndex;
        return prev.map((item, index) =>
          index === actualIndex
            ? {
                ...item,
                count: (item.count || 1) + 1,
                createdAt: now
              }
            : item
        );
      }

      const next = [...prev, { id: Date.now(), message, type, count: 1, createdAt: now }];
      return next.length > maxVisibleNotifications ? next.slice(next.length - maxVisibleNotifications) : next;
    });
    if (type === 'error') console.error("App Notification:", message);
  }, []);

  const removeNotification = useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const markInboxNotificationRead = useCallback(async (id: number) => {
    await backend.notifications.markAsRead(id);
    setCustomerInbox((prev) => prev.map((item) => (item.id === id ? { ...item, isRead: true } : item)));
  }, [backend.notifications]);

  const markAllInboxNotificationsRead = useCallback(async () => {
    const unread = customerInbox.filter((item) => !item.isRead);
    await Promise.all(unread.map((item) => backend.notifications.markAsRead(item.id)));
    setCustomerInbox((prev) => prev.map((item) => ({ ...item, isRead: true })));
  }, [backend.notifications, customerInbox]);

  const markAdminNotificationRead = useCallback(async (id: number) => {
    await backend.notifications.markAsRead(id);
    setAdminInbox((prev) => prev.map((item) => (item.id === id ? { ...item, isRead: true } : item)));
  }, [backend.notifications]);

  const markAllAdminNotificationsRead = useCallback(async () => {
    const unread = adminInbox.filter((item) => !item.isRead);
    await Promise.all(unread.map((item) => backend.notifications.markAsRead(item.id)));
    setAdminInbox((prev) => prev.map((item) => ({ ...item, isRead: true })));
  }, [adminInbox, backend.notifications]);

  const extractMsg = (err: any): string => {
    if (!err) return "Unknown Error";
    if (typeof err === 'string') return err;
    if (err.message && typeof err.message === 'string') {
        try {
            if (err.message.trim().startsWith('{')) {
                const parsed = JSON.parse(err.message);
                return parsed.message || parsed.error_description || err.message;
            }
        } catch (e) {}
        return err.message;
    }
    if (err.error_description) return err.error_description;
    if (err.msg) return err.msg;
    if (err.error && typeof err.error === 'string') return err.error;
    if (err.error && typeof err.error === 'object' && err.error.message) return err.error.message;
    if (err.code && err.details) return `Error ${err.code}: ${err.details}`;
    if (err.statusText) return err.statusText;
    if (err instanceof Error) return err.message;
    if (typeof err === 'object') {
        try {
            const str = JSON.stringify(err);
            if (str !== '{}' && str !== '[]') return str.replace(/[{}"]/g, ' ').trim();
        } catch (e) {}
    }
    const s = String(err);
    if (s === '[object Object]') {
        try { return JSON.stringify(err, null, 2); } catch (e) { return "An unexpected error occurred."; }
    }
    return s;
  };

  const isSupabaseMissingTables = (err: any) => {
      const msg = typeof err === 'string' ? err : (err.message || JSON.stringify(err));
      const code = err.code || '';
      if (code === '42P01') return true;
      if (code === 'PGRST205') return true;
      const lower = msg.toLowerCase();
      if (lower.includes('relation') && lower.includes('does not exist')) return true;
      if (lower.includes('schema cache')) return true;
      if (lower.includes('could not find the table')) return true;
      if (lower.includes('setup required')) return true;
      if (lower.includes('failed to fetch') || lower.includes('networkerror')) return false;
      return false;
  };

  const isAuthError = (err: any) => {
      const msg = typeof err === 'string' ? err : (err.message || "");
      const lower = msg.toLowerCase();
      return lower.includes('refresh token not found') || 
             lower.includes('invalid refresh token') || 
             lower.includes('session expired') || 
             lower.includes('invalid claim');
  };

  const refreshData = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return;
    }
    refreshInFlightRef.current = true;
    // PRE-FLIGHT CHECK: Avoid crashing if config is missing
    if (!hasValidConfig()) {
        console.warn("Backend configuration missing. Onboarding required.");
        setSetupRequired(true);
        setIsLoading(false);
        refreshInFlightRef.current = false;
        return;
    }

    try {
      const user = localStorage.getItem('agm_user');
      const isAuthenticatedBootstrap = Boolean(user);

      if (isAuthenticatedBootstrap) {
        const missingTables = await listMissingRequiredTables();
        if (missingTables.length > 0) {
            console.warn("Required workflow tables are missing:", missingTables);
            setSetupRequired(true);
            refreshInFlightRef.current = false;
            return;
        }
      }

      if (!backend || !backend.cms) {
          console.warn("Backend services not fully initialized yet. Retrying shortly...");
          return;
      }
      const fetchedSiteContent = await backend.cms.list();
      const contentMap: Record<string, any> = {};
      fetchedSiteContent.forEach(item => { contentMap[item.key] = item.value; });
      setSiteContent(contentMap);

      if (!isAuthenticatedBootstrap) {
          if (localStorage.getItem('agm_user')) {
              refreshInFlightRef.current = false;
              await refreshData();
              return;
          }
          setCurrentUser(null);
          setIsAuthenticated(false);
          setProducts([]);
          setCategories([]);
          setOrders([]);
          setInvoices([]);
          setCustomers([]);
          setTestimonials([]);
          setInquiries([]);
          setOrderEvents([]);
          setShipments([]);
          setInventoryItems([]);
          setInventoryUsageLogs([]);
          setBranches([]);
          setPaymentTransactions([]);
          setCustomerInbox([]);
          setAdminInbox([]);
          setSetupRequired(false);
          return;
      }

      if (!backend.products || !backend.categories || !backend.inquiries) {
          console.warn("Protected backend services not fully initialized yet. Retrying shortly...");
          return;
      }

      const [fetchedProducts, fetchedCategories, fetchedInquiries] = await Promise.all([
        backend.products.list(),
        backend.categories.list(),
        backend.inquiries.list()
      ]);
      setProducts(fetchedProducts);
      setCategories(fetchedCategories);
      setInquiries(fetchedInquiries);

      if (user) {
          const parsedUser = JSON.parse(user);
          try {
              let requiresSetup = false;
              if (!backend.orders || !backend.invoices || !backend.customers) throw new Error("Protected backend services unavailable");
              const [fetchedOrders, fetchedInvoices, fetchedCustomers, fetchedTestimonials] = await Promise.all([
                  backend.orders.list(),
                  backend.invoices.list(),
                  backend.customers.list(),
                  backend.testimonials.list()
              ]);
              const syncedUser = fetchedCustomers.find((c) => c.id === parsedUser.id) || parsedUser;
              setOrders(fetchedOrders);
              setInvoices(fetchedInvoices);
              setCustomers(fetchedCustomers);
              setTestimonials(fetchedTestimonials);
              setCurrentUser(syncedUser);
              localStorage.setItem('agm_user', JSON.stringify(syncedUser));
              setIsAuthenticated(true);

              if (backend.orders.listEvents) {
                try {
                  const fetchedEvents = await backend.orders.listEvents();
                  setOrderEvents(fetchedEvents);
                } catch (evtErr) {
                  if (isSupabaseMissingTables(evtErr)) requiresSetup = true;
                  console.warn('Order events load skipped:', evtErr);
                }
              }

              if (backend.orders.listShipments) {
                try {
                  const fetchedShipments = await backend.orders.listShipments();
                  setShipments(fetchedShipments);
                } catch (shipErr) {
                  if (isSupabaseMissingTables(shipErr)) requiresSetup = true;
                  console.warn('Shipments load skipped:', shipErr);
                }
              }

              if (backend.payments?.listTransactions) {
                try {
                  const fetchedTx = await backend.payments.listTransactions();
                  setPaymentTransactions(fetchedTx);
                } catch (paymentErr) {
                  if (isSupabaseMissingTables(paymentErr)) requiresSetup = true;
                  console.warn('Payment transactions load skipped:', paymentErr);
                }
              }

              const organizationAdminId = resolveOrganizationAdminId(syncedUser);
              const branchScopeOrgId = isCustomerRole(syncedUser.role) ? organizationAdminId : undefined;
              if (backend.branches?.list) {
                try {
                  const fetchedBranches = await backend.branches.list(branchScopeOrgId || undefined);
                  setBranches(fetchedBranches);
                } catch (branchErr) {
                  if (isSupabaseMissingTables(branchErr)) requiresSetup = true;
                  console.warn('Branch load skipped:', branchErr);
                }
              } else {
                setBranches([]);
              }
              if (organizationAdminId && backend.inventory?.list) {
                try {
                  const fetchedInventory = await backend.inventory.list(organizationAdminId);
                  setInventoryItems(fetchedInventory);
                  if (backend.inventory.listUsageLogs) {
                    const fetchedUsageLogs = await backend.inventory.listUsageLogs(organizationAdminId);
                    setInventoryUsageLogs(fetchedUsageLogs);
                  } else {
                    setInventoryUsageLogs([]);
                  }
                } catch (invErr) {
                  if (isSupabaseMissingTables(invErr)) requiresSetup = true;
                  console.warn('Inventory load skipped:', invErr);
                }
              } else {
                setInventoryItems([]);
                setInventoryUsageLogs([]);
              }
              if (isCustomerRole(syncedUser.role)) {
                try {
                  await callAuthorizedApi('/api/customer-notifications/sync', { method: 'POST' });
                  const fetchedInbox = await backend.notifications.list(syncedUser.id);
                  setCustomerInbox(fetchedInbox);
                  setAdminInbox([]);
                } catch (notificationErr) {
                  if (isSupabaseMissingTables(notificationErr)) requiresSetup = true;
                  console.warn('Customer notification sync skipped:', notificationErr);
                }
              } else {
                setCustomerInbox([]);
                try {
                  const adminInbox = await backend.notifications.list(syncedUser.id);
                  setAdminInbox(adminInbox);
                  const unreadSystemKeys = new Set<string>();
                  adminInbox
                    .filter((item) => !item.isRead)
                    .forEach((item) => {
                      const key = item.externalKey || `notification-${item.id}`;
                      unreadSystemKeys.add(key);
                      if (!unreadSystemNotificationKeysRef.current.has(key)) {
                        addNotification(item.title ? `${item.title}: ${item.message}` : item.message, item.type);
                      }
                    });
                  unreadSystemNotificationKeysRef.current = unreadSystemKeys;
                } catch (notificationErr) {
                  if (isSupabaseMissingTables(notificationErr)) requiresSetup = true;
                  console.warn('Admin notification load skipped:', notificationErr);
                }
              }
              if (requiresSetup) {
                  setSetupRequired(true);
                  return;
              }
          } catch (authErr: any) {
              console.warn("Protected data initialization failed:", authErr.message);
              // If it's a specific auth failure like 'Refresh Token Not Found', perform a full cleanup
              if (isAuthError(authErr)) {
                  console.log("Authentication session has expired. Redirecting to home.");
                  localStorage.removeItem('agm_user');
                  setCurrentUser(null);
                  setIsAuthenticated(false);
                  setCustomerInbox([]);
                  setAdminInbox([]);
              } else if (isSupabaseMissingTables(authErr)) {
                  setSetupRequired(true);
              }
          }
      } else {
          setCurrentUser(null);
          setIsAuthenticated(false);
          setCustomerInbox([]);
          setAdminInbox([]);
      }
      setSetupRequired(false);
    } catch (error: any) {
      if (isSupabaseMissingTables(error)) {
          console.warn("Application requires setup or connection config:", error.message || error);
          setSetupRequired(true);
      } else if (isAuthError(error)) {
          console.log("Refresh data failed due to invalid auth session.");
          localStorage.removeItem('agm_user');
          setCurrentUser(null);
          setIsAuthenticated(false);
          setCustomerInbox([]);
          setAdminInbox([]);
      } else if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
          console.warn("Network Error during refresh:", error.message);
      } else {
          const errorDetails = error instanceof Error ? { message: error.message, stack: error.stack, name: error.name } : error;
          console.error("Refresh Data Error Details:", JSON.stringify(errorDetails, null, 2));
          const msg = extractMsg(error);
          addNotification(`Failed to load data: ${msg}`, 'error');
      }
    } finally {
	      refreshInFlightRef.current = false;
	      setIsLoading(false);
	    }
  }, [addNotification, backend, callAuthorizedApi, isCustomerRole, resolveOrganizationAdminId]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') {
      return;
    }

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshData();
      }
    };

    const intervalId = window.setInterval(refreshIfVisible, 30000);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [isAuthenticated, refreshData]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const user = await backend.auth.login(email, password);
      if (!user.isApproved && !adminRoles.includes(user.role)) {
          addNotification("Your account is pending approval by an administrator.", "error");
          await backend.auth.logout();
          setIsLoading(false);
          return null;
      }
      setCurrentUser(user);
      setIsAuthenticated(true);
      localStorage.setItem('agm_user', JSON.stringify(user));
      addNotification(`Welcome back, ${user.name}!`, 'success');
      await waitForRefreshToFinish(() => refreshInFlightRef.current);
      await refreshData();
      return user;
    } catch (error: any) {
      if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
          addNotification('Connection failed. Please check your internet connection or Database Settings.', 'error');
      } else {
          const msg = extractMsg(error);
          // Only show toast if it's not a setup requirement error
          if (!isSupabaseMissingTables(error)) {
              addNotification(`Login failed: ${msg}`, 'error');
          }
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (data: NewCustomerData) => {
    setIsLoading(true);
    try {
      await backend.auth.signup(data);
      addNotification('Organization owner account created. Please log in.', 'success');
      return true;
    } catch (error: any) {
      if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
          addNotification('Signup failed: Connection error. Check settings.', 'error');
      } else {
          const msg = extractMsg(error);
          addNotification(`Signup failed: ${msg}`, 'error');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    setIsLoading(true);
    try {
      await backend.auth.forgotPassword(email);
      addNotification('Password reset link sent to your email.', 'success');
      return true;
    } catch (error) {
      const msg = extractMsg(error);
      addNotification(`Failed to send reset link: ${msg}`, 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const switchUser = async (id: number | string, role: UserRole) => {
      addNotification("User switching is only available in Mock mode or with Admin API.", "info");
  };

  const addCategory = async (name: string, parentId: number | null) => {
      try {
          const newCat = await backend.categories.add(name, parentId);
          setCategories(prev => [...prev, newCat]);
          addNotification('Category added', 'success');
      } catch (e) { addNotification(extractMsg(e), 'error'); }
  };

  const updateCategory = async (id: number, name: string, parentId: number | null) => {
      try {
          const updated = await backend.categories.update(id, name, parentId);
          setCategories(prev => prev.map(c => c.id === id ? updated : c));
          addNotification('Category updated', 'success');
      } catch (e) { addNotification(extractMsg(e), 'error'); }
  };

  const deleteCategory = async (id: number) => {
      try {
          await backend.categories.delete(id);
          setCategories(prev => prev.filter(c => c.id !== id));
          addNotification('Category deleted', 'success');
      } catch (e) { addNotification(extractMsg(e), 'error'); }
  };

  const addProduct = async (data: NewProductData) => {
    try {
      const newProduct = await backend.products.add(data);
      setProducts([...products, newProduct]);
      addNotification('Product added successfully', 'success');
    } catch (error) { addNotification(`Failed to add product: ${extractMsg(error)}`, 'error'); }
  };

  const updateProduct = async (id: number, data: Partial<NewProductData>) => {
    try {
      const updatedProduct = await backend.products.update(id, data);
      setProducts(products.map(p => p.id === id ? updatedProduct : p));
      addNotification('Product updated successfully', 'success');
    } catch (error) { addNotification(`Failed to update product: ${extractMsg(error)}`, 'error'); }
  };

  const deleteProduct = async (id: number) => {
    try {
      await backend.products.delete(id);
      setProducts(products.filter(p => p.id !== id));
      addNotification('Product deleted successfully', 'success');
    } catch (error) { addNotification(`Failed to delete product: ${extractMsg(error)}`, 'error'); }
  };

  const createOrder = async (data: NewOrderData) => {
    try {
      const organizationAdminId = resolveOrganizationAdminId(currentUser);
      const owningCustomerId = getOrderOwningCustomerId(currentUser) || data.customerId;
      const payload: CreateOrderData = {
        ...data,
        customerId: owningCustomerId,
        submittedById: currentUser?.id,
        customerAdminId: data.customerAdminId || organizationAdminId || owningCustomerId
      };
      const newOrder = await backend.orders.create(payload);
      setOrders(prev => [newOrder, ...prev]);
      addNotification('Order placed successfully!', 'success');
    } catch (error) { addNotification(`Failed to place order: ${extractMsg(error)}`, 'error'); }
  };

  const amendDeniedOrder = async (orderId: number, data: NewOrderData) => {
    try {
      if (!currentUser || !isCustomerRole(currentUser.role)) {
        throw new Error('Only customer organization members can edit purchase orders in the owner-approval stage.');
      }
      const organizationAdminId = resolveOrganizationAdminId(currentUser);
      const owningCustomerId = getOrderOwningCustomerId(currentUser) || data.customerId;
      const payload: CreateOrderData = {
        ...data,
        customerId: owningCustomerId,
        submittedById: currentUser.id,
        customerAdminId: data.customerAdminId || organizationAdminId || owningCustomerId
      };
      const response = await callAuthorizedApi('/api/order-actions', {
        method: 'POST',
        body: JSON.stringify({
          action: 'amend_denied_order',
          orderId,
          order: payload
        })
      });
      const updatedOrder = hydrateOrderDates(response.order);
      setOrders(prev => prev.map((order) => (order.id === orderId ? updatedOrder : order)));
      if (backend.orders.listEvents) {
        const events = await backend.orders.listEvents(orderId);
        setOrderEvents((prev) => {
          const rest = prev.filter((ev) => ev.orderId !== orderId);
          return [...events, ...rest].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        });
      }
      addNotification(`Order #${orderId} updated and kept in the organization approval flow.`, 'success');
    } catch (error) {
      addNotification(`Failed to update order: ${extractMsg(error)}`, 'error');
    }
  };

  const updateOrderStatus = async (id: number, status: OrderStatus, prices?: Record<string, number>) => {
    try {
      let updatedOrder: Order;
      let invoice: Invoice | undefined;
      if (
        currentUser &&
        isCustomerRole(currentUser.role) &&
        [OrderStatus.COMPLETED, OrderStatus.ISSUE_REPORTED].includes(status)
      ) {
        const payload = await callAuthorizedApi('/api/order-actions', {
          method: 'POST',
          body: JSON.stringify({
            action: 'update_customer_status',
            orderId: id,
            status
          })
        });
        updatedOrder = hydrateOrderDates(payload.order);
      } else {
        const response = await backend.orders.updateStatus(
          id,
          status,
          prices,
          currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
        );
        updatedOrder = response.order;
        invoice = response.invoice;
      }
      setOrders(prev => prev.map(o => o.id === id ? updatedOrder : o));
      if (
        currentUser &&
        isCustomerRole(currentUser.role) &&
        [OrderStatus.COMPLETED, OrderStatus.ISSUE_REPORTED].includes(status)
      ) {
        await refreshFinancialData();
      }
      if (invoice) {
          setInvoices(prev => {
              const exists = prev.some(inv => inv.id === invoice.id);
              if (exists) return prev.map(inv => inv.id === invoice.id ? invoice : inv);
              return [...prev, invoice];
          });
      }
      if (backend.orders.listEvents) {
        try {
          const events = await backend.orders.listEvents(id);
          setOrderEvents((prev) => {
            const rest = prev.filter((ev) => ev.orderId !== id);
            return [...events, ...rest].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          });
        } catch (evtErr) {
          console.warn('Order events refresh skipped:', evtErr);
        }
      }
      addNotification(`Order #${id} marked as ${status}`, 'success');
    } catch (error) { addNotification(`Failed to update status: ${extractMsg(error)}`, 'error'); }
  };

  const reviewCustomerOrder = async (orderId: number, approved: boolean, remark?: string) => {
    try {
      if (!isCustomerRole(currentUser?.role) && !backend.orders.reviewCustomerApproval) {
        throw new Error('Customer approval workflow is not available in this backend.');
      }
      const updated =
        currentUser && isCustomerRole(currentUser.role)
          ? (await callAuthorizedApi('/api/order-actions', {
              method: 'POST',
              body: JSON.stringify({
                action: 'review_customer_order',
                orderId,
                approved,
                remark
              })
            })).order
          : null;
      const normalizedUpdated =
        updated
          ? hydrateOrderDates(updated)
          : await backend.orders.reviewCustomerApproval(
              orderId,
              approved,
              remark,
              currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
            );
      setOrders(prev => prev.map(o => o.id === orderId ? normalizedUpdated : o));
      if (backend.orders.listEvents) {
        const events = await backend.orders.listEvents(orderId);
        setOrderEvents((prev) => {
          const rest = prev.filter((ev) => ev.orderId !== orderId);
          return [...events, ...rest].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        });
      }
      addNotification(
        approved ? `Order #${orderId} approved and sent to admin queue.` : `Order #${orderId} denied with remark.`,
        approved ? 'success' : 'info'
      );
    } catch (error) {
      addNotification(`Failed to review order: ${extractMsg(error)}`, 'error');
    }
  };

  const shipOrder = async (orderId: number, trackingNumber: string, courierPartner?: string) => {
    try {
      if (!backend.orders.ship) {
        throw new Error('Shipment tracking is not available in this backend.');
      }
      const shipment = await backend.orders.ship(
        orderId,
        trackingNumber,
        courierPartner,
        currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
      );
      setShipments(prev => {
        const next = prev.filter((s) => s.orderId !== orderId);
        return [shipment, ...next];
      });
      await refreshData();
      addNotification(`Shipment created for order #${orderId}.`, 'success');
    } catch (error) {
      addNotification(`Failed to create shipment: ${extractMsg(error)}`, 'error');
    }
  };

  const markShipmentDelivered = async (orderId: number) => {
    try {
      if (!backend.orders.markDelivered) {
        throw new Error('Shipment delivery confirmation is not available in this backend.');
      }
      const shipment = await backend.orders.markDelivered(
        orderId,
        currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
      );
      if (shipment) {
        setShipments(prev => {
          const next = prev.filter((s) => s.orderId !== orderId);
          return [shipment, ...next];
        });
        addNotification(`Order #${orderId} marked as delivered in shipment log.`, 'success');
      }
    } catch (error) {
      addNotification(`Failed to mark shipment delivered: ${extractMsg(error)}`, 'error');
    }
  };

  const setOrderItemPacked = async (orderId: number, itemId: number, packed: boolean) => {
      try {
          if (backend.orders.setItemPacked) {
              await backend.orders.setItemPacked(
                orderId,
                itemId,
                packed,
                currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
              );
          }
      } catch (error) {
          addNotification(`Failed to update packed checklist: ${extractMsg(error)}`, 'error');
      }
      setOrders((prev) =>
          prev.map((order) =>
              order.id === orderId
                  ? {
                        ...order,
                        items: order.items.map((item) =>
                            item.id === itemId ? { ...item, packed } : item
                        )
                    }
                  : order
          )
      );
  };

  const submitOrderReceiptChecks = async (
      orderId: number,
      checks: Array<{ itemId?: number; receivedQuantity: number; remark?: string }>
  ) => {
      try {
          if (currentUser && isCustomerRole(currentUser.role)) {
              const payload = await callAuthorizedApi('/api/order-actions', {
                method: 'POST',
                body: JSON.stringify({
                  action: 'submit_receipt_checks',
                  orderId,
                  checks
                })
              });
              setOrders((prev) => prev.map((order) => (order.id === orderId ? hydrateOrderDates(payload.order) : order)));
          } else {
              if (backend.orders.submitReceiptChecks) {
                  await backend.orders.submitReceiptChecks(
                    orderId,
                    checks,
                    currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
                  );
              }
              setOrders((prev) =>
                  prev.map((order) =>
                      order.id === orderId
                          ? {
                                ...order,
                                items: order.items.map((item) => {
                                    const matched = checks.find((check) => check.itemId === item.id);
                                    if (!matched) return item;
                                    return {
                                        ...item,
                                        receivedQuantity: matched.receivedQuantity,
                                        receiveRemark: matched.remark || ''
                                    };
                                })
                            }
                          : order
                  )
              );
          }
      } catch (error) {
          addNotification(`Failed to save receiving checklist: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const syncInventoryFromReceipt = async (
      orderId: number,
      checks: Array<{ itemId?: number; receivedQuantity: number; remark?: string }>
  ) => {
      try {
          const organizationAdminId = resolveOrganizationAdminId(currentUser);
          if (!organizationAdminId || !backend.inventory?.applyReceipt) return;
          const order = orders.find((o) => o.id === orderId);
          if (!order) return;
          const branchId = order.branchId ?? null;
          const receiptItems = order.items
            .map((item) => {
              const matched = checks.find((check) => check.itemId === item.id);
              const qty = Number(matched?.receivedQuantity || 0);
              if (qty <= 0) return null;
              return {
                productId: item.productId,
                productName: item.productName,
                variationId: item.variationId,
                variationName: item.variationName,
                quantity: qty
              };
            })
            .filter(Boolean) as Array<{
              productId: number;
              productName: string;
              variationId?: string;
              variationName?: string;
              quantity: number;
            }>;

          if (receiptItems.length === 0) return;
          await backend.inventory.applyReceipt(organizationAdminId, branchId, receiptItems);
          const [refreshed, refreshedLogs] = await Promise.all([
            backend.inventory.list(organizationAdminId),
            backend.inventory.listUsageLogs ? backend.inventory.listUsageLogs(organizationAdminId) : Promise.resolve([])
          ]);
          setInventoryItems(refreshed);
          setInventoryUsageLogs(refreshedLogs);
      } catch (error) {
          addNotification(`Failed to sync persistent inventory: ${extractMsg(error)}`, 'error');
      }
  };

  const updateInventoryThreshold = async (branchId: number | null | undefined, productId: number, variationId: string | undefined, threshold: number) => {
      try {
          const organizationAdminId = resolveOrganizationAdminId(currentUser);
          if (!organizationAdminId || !backend.inventory?.updateThreshold || !backend.inventory?.list) return;
          await backend.inventory.updateThreshold(organizationAdminId, branchId, productId, variationId, threshold);
          const refreshed = await backend.inventory.list(organizationAdminId);
          setInventoryItems(refreshed);
          addNotification('Reorder threshold updated.', 'success');
      } catch (error) {
          addNotification(`Failed to update threshold: ${extractMsg(error)}`, 'error');
      }
  };

  const issueInventoryStock = async (inventoryItemId: number | string, quantity: number, note?: string) => {
      try {
          const organizationAdminId = resolveOrganizationAdminId(currentUser);
          if (!organizationAdminId || !backend.inventory?.issueStock || !backend.inventory?.list) return;
          await backend.inventory.issueStock(
            organizationAdminId,
            inventoryItemId,
            quantity,
            note,
            currentUser ? { id: currentUser.id, name: currentUser.name } : undefined
          );
          const [refreshedInventory, refreshedLogs] = await Promise.all([
            backend.inventory.list(organizationAdminId),
            backend.inventory.listUsageLogs ? backend.inventory.listUsageLogs(organizationAdminId) : Promise.resolve([])
          ]);
          setInventoryItems(refreshedInventory);
          setInventoryUsageLogs(refreshedLogs);
          addNotification('Inventory usage recorded successfully.', 'success');
      } catch (error) {
          addNotification(`Failed to record inventory usage: ${extractMsg(error)}`, 'error');
      }
  };

  const markInvoiceAsPaid = async (id: number) => {
      try {
          const updatedInv = await backend.invoices.pay(id);
          setInvoices(prev => prev.map(i => i.id === id ? updatedInv : i));
          setOrders(prev => prev.map(o => o.id === updatedInv.orderId ? { ...o, status: OrderStatus.PAID } : o));
          addNotification(`Invoice #${id} paid!`, 'success');
      } catch (error) { addNotification(`Failed to pay invoice: ${extractMsg(error)}`, 'error'); }
  };

  const processInvoicePayment = async (invoiceId: number, provider = 'SIMULATED_GATEWAY') => {
      try {
          const targetInvoice = invoices.find((inv) => inv.id === invoiceId);
          if (!targetInvoice) throw new Error(`Invoice #${invoiceId} not found.`);
          const normalizedProvider = provider.toUpperCase();
          const payableAmount = getInvoicePayableAmount(targetInvoice);

          if (normalizedProvider === 'RAZORPAY') {
            const scriptLoaded = await loadRazorpayCheckoutScript();
            if (!scriptLoaded || !(window as any).Razorpay) {
              throw new Error('Unable to load Razorpay checkout.');
            }
            const { data: sessionData } = await getClient().auth.getSession();
            const accessToken = sessionData?.session?.access_token;
            if (!accessToken) {
              throw new Error('Your session has expired. Please log in again before paying.');
            }
            const razorpayApiHeaders: Record<string, string> = {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`
            };

            const createOrderResponse = await fetch(getApiUrl('/api/razorpay/create-order'), {
              method: 'POST',
              headers: razorpayApiHeaders,
              body: JSON.stringify({ invoiceId })
            });
            const createOrderPayload = await createOrderResponse.json();
            if (!createOrderResponse.ok || !createOrderPayload?.orderId) {
              throw new Error(createOrderPayload?.message || 'Failed to create Razorpay order.');
            }

            await new Promise<void>((resolve) => {
              let finished = false;
              const finish = () => {
                if (finished) return;
                finished = true;
                resolve();
              };
              const Razorpay = (window as any).Razorpay;
              const instance = new Razorpay({
                key: createOrderPayload.keyId,
                amount: createOrderPayload.amount,
                currency: createOrderPayload.currency,
                name: siteContent?.footer_info?.companyName || 'AGM Portal',
                description: `Invoice #${invoiceId}`,
                order_id: createOrderPayload.orderId,
                prefill: {
                  name: currentUser?.name || '',
                  email: currentUser?.email || '',
                  contact: currentUser?.phone || ''
                },
                notes: {
                  invoice_id: String(invoiceId),
                  order_id: String(targetInvoice.orderId)
                },
                theme: {
                  color: '#4f46e5'
                },
                handler: async (response: any) => {
                  try {
                    const verifyResponse = await fetch(getApiUrl('/api/razorpay/verify'), {
                      method: 'POST',
                      headers: razorpayApiHeaders,
                      body: JSON.stringify({
                        invoiceId,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature
                      })
                    });
                    const verifyPayload = await verifyResponse.json();
                    if (!verifyResponse.ok || !verifyPayload?.verified) {
                      throw new Error(verifyPayload?.message || 'Payment signature verification failed.');
                    }

                    await refreshFinancialData();
                    addNotification(`Razorpay payment successful for invoice #${invoiceId}.`, 'success');
                    finish();
                  } catch (verificationError) {
                    addNotification(`Payment verification failed: ${extractMsg(verificationError)}`, 'error');
                    finish();
                  }
                },
                modal: {
                  ondismiss: async () => {
                    if (finished) return;
                    addNotification('Payment was cancelled.', 'info');
                    finish();
                  }
                }
              });

              instance.on('payment.failed', async (response: any) => {
                addNotification(`Payment failed: ${response?.error?.description || 'Unknown error'}`, 'error');
                finish();
              });

              instance.open();
            });

            if (backend.payments?.listTransactions) {
              const tx = await backend.payments.listTransactions();
              setPaymentTransactions(tx);
            }
            return;
          }

          if (!backend.payments?.recordTransaction) {
            await markInvoiceAsPaid(invoiceId);
            return;
          }
          const gatewayRef = `TXN-${invoiceId}-${Date.now()}`;
          await backend.payments.recordTransaction({
            invoiceId: targetInvoice.id,
            orderId: targetInvoice.orderId,
            amount: payableAmount,
            provider: normalizedProvider,
            status: 'SUCCESS',
            gatewayTransactionId: gatewayRef,
            payload: { simulated: true, capturedAt: new Date().toISOString() },
            createdBy: currentUser?.id
          });
          if (backend.payments.reconcileInvoice) {
            const result = await backend.payments.reconcileInvoice(invoiceId);
            if (result.isSettled) {
              await markInvoiceAsPaid(invoiceId);
            }
          } else {
            await markInvoiceAsPaid(invoiceId);
          }
          if (backend.payments.listTransactions) {
            const tx = await backend.payments.listTransactions();
            setPaymentTransactions(tx);
          }
      } catch (error) {
          addNotification(`Payment processing failed: ${extractMsg(error)}`, 'error');
      }
  };

  const requestInvoiceRefund = async (invoiceId: number, amount?: number, notes?: string) => {
      try {
          const { data: sessionData } = await getClient().auth.getSession();
          const accessToken = sessionData?.session?.access_token;
          if (!accessToken) {
              throw new Error('Your session has expired. Please log in again before requesting a refund.');
          }
          const response = await fetch(getApiUrl('/api/razorpay/refund'), {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${accessToken}`
              },
              body: JSON.stringify({
                  invoiceId,
                  amount,
                  notes
              })
          });
          const payload = await response.json();
          if (!response.ok || !payload?.success) {
              throw new Error(payload?.message || 'Refund request failed.');
          }
          await refreshFinancialData();
          addNotification(`Refund initiated for invoice #${invoiceId}.`, 'success');
      } catch (error) {
          addNotification(`Refund failed: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const downloadPaymentReceipt = (invoiceId: number, transactionId?: number) => {
      const invoice = invoices.find((item) => item.id === invoiceId);
      if (!invoice) {
          addNotification(`Invoice #${invoiceId} not found for receipt download.`, 'error');
          return;
      }
      const invoiceTransactions = paymentTransactions.filter((transaction) => transaction.invoiceId === invoiceId);
      const latestByInvoice = getLatestTransactionByInvoice(invoiceTransactions);
      const preferredTransaction =
        invoiceTransactions.find((item) => ['RECONCILED', 'SUCCESS'].includes(item.status)) || latestByInvoice[invoiceId];
      const transaction = transactionId
        ? invoiceTransactions.find((item) => item.id === transactionId)
        : preferredTransaction;
      if (!transaction) {
          addNotification(`No payment record found for invoice #${invoiceId}.`, 'error');
          return;
      }

      const order = orders.find((item) => item.id === invoice.orderId);
      const customer = customers.find((item) => item.id === invoice.customerId);
      const html = buildReceiptDocumentHtml({
          companyName: siteContent?.footer_info?.companyName || 'AGM Portal',
          invoice,
          customerName: customer?.name || invoice.customerName,
          orderLabel: `Order #${invoice.orderId}`,
          transaction
      });

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildReceiptLabel(invoiceId, transaction.id);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
  };

  const reconcileInvoice = async (invoiceId: number) => {
      try {
          if (!backend.payments?.reconcileInvoice) return;
          const result = await backend.payments.reconcileInvoice(invoiceId);
          if (result.isSettled) {
            await markInvoiceAsPaid(invoiceId);
          }
          if (backend.payments.listTransactions) {
            const tx = await backend.payments.listTransactions();
            setPaymentTransactions(tx);
          }
          addNotification(
            result.isSettled
              ? `Invoice #${invoiceId} reconciled successfully.`
              : `Invoice #${invoiceId} is not fully settled yet.`,
            result.isSettled ? 'success' : 'info'
          );
      } catch (error) {
          addNotification(`Reconciliation failed: ${extractMsg(error)}`, 'error');
      }
  };

  const updateInvoice = async (id: number, updates: Partial<Invoice>) => {
      try {
          const updated = await backend.invoices.update(id, updates);
          setInvoices(prev => prev.map(i => i.id === id ? updated : i));
          addNotification('Invoice updated', 'success');
      } catch (error) { addNotification(`Failed to update invoice: ${extractMsg(error)}`, 'error'); }
  };

  const addCustomer = async (data: NewCustomerData) => {
      try {
          const newCust = await backend.customers.add(data);
          setCustomers(prev => [...prev, newCust]);
          addNotification('Customer added', 'success');
      } catch (error) { addNotification(`Failed to add customer: ${extractMsg(error)}`, 'error'); }
  };

  const addAdmin = async (data: NewCustomerData) => {
      try {
          const newAdmin = await backend.customers.addAdmin(data);
          setCustomers(prev => [...prev, newAdmin]);
          addNotification('Admin added', 'success');
      } catch (error) { addNotification(`Failed to add admin: ${extractMsg(error)}`, 'error'); }
  };

  const createCustomerOrgMember = async (
      data: NewCustomerData,
      role: UserRole.CUSTOMER | UserRole.CUSTOMER_MANAGER | UserRole.CUSTOMER_BRANCH_ADMIN = UserRole.CUSTOMER
  ) => {
      try {
          if (!currentUser || ![UserRole.CUSTOMER_ADMIN, UserRole.CUSTOMER_BRANCH_ADMIN].includes(currentUser.role)) {
              throw new Error('Only customer admins can create customer team members.');
          }
          await callAuthorizedApi('/api/customer-org/members', {
              method: 'POST',
              body: JSON.stringify({
                ...data,
                role,
                billingAddress: data.billingAddress || currentUser.billingAddress,
                shippingAddress: data.shippingAddress || currentUser.shippingAddress
              })
          });
          const refreshedCustomers = await backend.customers.list();
          setCustomers(refreshedCustomers);
          addNotification(
            `${role === UserRole.CUSTOMER_BRANCH_ADMIN ? 'Branch customer admin' : role === UserRole.CUSTOMER_MANAGER ? 'Customer manager' : 'Customer user'} added.`,
            'success'
          );
      } catch (error) {
          addNotification(`Failed to add team member: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const addBranch = async (data: NewCustomerBranchData) => {
      try {
          if (!backend.branches?.add) {
              throw new Error('Branch management is not available in this backend.');
          }
          const created = await backend.branches.add(data);
          setBranches(prev => {
              const normalized = prev.map((branch) =>
                created.isDefault && branch.organizationAdminId === created.organizationAdminId
                  ? { ...branch, isDefault: false }
                  : branch
              );
              return [created, ...normalized];
          });
          addNotification('Branch added successfully.', 'success');
      } catch (error) {
          addNotification(`Failed to add branch: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const updateBranch = async (id: number, data: Partial<NewCustomerBranchData>) => {
      try {
          if (!backend.branches?.update) {
              throw new Error('Branch management is not available in this backend.');
          }
          const updated = await backend.branches.update(id, data);
          setBranches(prev => prev.map((branch) => {
              if (branch.organizationAdminId === updated.organizationAdminId && updated.isDefault && branch.id !== updated.id) {
                  return { ...branch, isDefault: false };
              }
              return branch.id === id ? updated : branch;
          }));
          addNotification('Branch updated successfully.', 'success');
      } catch (error) {
          addNotification(`Failed to update branch: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const deleteBranch = async (id: number) => {
      try {
          if (!backend.branches?.delete) {
              throw new Error('Branch management is not available in this backend.');
          }
          await backend.branches.delete(id);
          setBranches(prev => prev.filter((branch) => branch.id !== id));
          setInventoryItems(prev => prev.filter((item) => item.branchId !== id));
          addNotification('Branch removed successfully.', 'success');
      } catch (error) {
          addNotification(`Failed to delete branch: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const updateBranchProductPrice = async (
      branchId: number,
      productId: number,
      variationId: string | undefined,
      price: number | null
  ) => {
      try {
          const branch = branches.find((item) => item.id === branchId);
          if (!branch || !backend.branchPricing) {
              throw new Error('Branch pricing is not available right now.');
          }
          if (price === null) {
              await backend.branchPricing.remove(branchId, productId, variationId);
          } else {
              await backend.branchPricing.upsert({
                  organizationAdminId: branch.organizationAdminId,
                  branchId,
                  productId,
                  variationId,
                  price
              });
          }
          const refreshedProducts = await backend.products.list();
          setProducts(refreshedProducts);
          addNotification('Branch pricing updated.', 'success');
      } catch (error) {
          addNotification(`Failed to update branch pricing: ${extractMsg(error)}`, 'error');
          throw error;
      }
  };

  const updateCustomer = async (id: number | string, data: Partial<NewCustomerData>) => {
      try {
          const shouldUseTeamApi = currentUser?.role === UserRole.CUSTOMER_ADMIN && currentUser.id !== id;
          const updated = shouldUseTeamApi
            ? (await callAuthorizedApi('/api/customer-org/members', {
                method: 'PATCH',
                body: JSON.stringify({
                  memberId: id,
                  ...data
                })
              })).member as User
            : await backend.customers.update(id, data);
          setCustomers(prev => prev.map(c => c.id === id ? updated : c));
          if (currentUser && currentUser.id === id) {
              const updatedUser = { ...currentUser, ...updated };
              setCurrentUser(updatedUser);
              localStorage.setItem('agm_user', JSON.stringify(updatedUser));
          }
          addNotification('Profile updated', 'success');
      } catch (error) { addNotification(`Failed to update: ${extractMsg(error)}`, 'error'); }
  };

  const updateCustomerRole = async (id: number | string, role: UserRole) => {
      try {
          if (currentUser?.role === UserRole.CUSTOMER_ADMIN) {
              const payload = await callAuthorizedApi('/api/customer-org/members', {
                method: 'PATCH',
                body: JSON.stringify({
                  memberId: id,
                  role
                })
              });
              setCustomers(prev => prev.map(c => c.id === id ? payload.member : c));
          } else if (backend.customers.updateRole) {
              await backend.customers.updateRole(id, role);
              setCustomers(prev => prev.map(c => c.id === id ? { ...c, role } : c));
          }
          addNotification(`User role updated to ${role}`, 'success');
      } catch (error) { addNotification(`Failed to update role: ${extractMsg(error)}`, 'error'); }
  };

  const approveCustomer = async (id: number | string) => {
      try {
          if (currentUser?.role === UserRole.CUSTOMER_ADMIN) {
              const payload = await callAuthorizedApi('/api/customer-org/members', {
                method: 'PATCH',
                body: JSON.stringify({
                  memberId: id,
                  isApproved: true
                })
              });
              setCustomers(prev => prev.map(c => c.id === id ? payload.member : c));
          } else if (backend.customers.updateApproval) {
              await backend.customers.updateApproval(id, true);
              setCustomers(prev => prev.map(c => c.id === id ? { ...c, isApproved: true } : c));
          }
          addNotification(`User approved`, 'success');
      } catch (error) { addNotification(`Failed to approve: ${extractMsg(error)}`, 'error'); }
  };

  const rejectCustomer = async (id: number | string) => {
      try {
          if (backend.customers.updateApproval) {
              await backend.customers.updateApproval(id, false);
              setCustomers(prev => prev.map(c => c.id === id ? { ...c, isApproved: false } : c));
              addNotification(`User disallowed`, 'success');
          }
      } catch (error) { addNotification(`Failed to reject: ${extractMsg(error)}`, 'error'); }
  };

  const deleteCustomer = async (id: number | string) => {
      try {
          if (currentUser?.role === UserRole.CUSTOMER_ADMIN) {
              await callAuthorizedApi('/api/customer-org/members', {
                method: 'DELETE',
                body: JSON.stringify({ memberId: id })
              });
          } else if (currentUser?.role && adminRoles.includes(currentUser.role)) {
              await callAuthorizedApi('/api/admin/users', {
                method: 'DELETE',
                body: JSON.stringify({ memberId: id })
              });
          } else {
              await backend.customers.delete(id);
          }
          setCustomers(prev => prev.filter(c => c.id !== id));
          addNotification('User deleted', 'success');
      } catch (error) { addNotification(`Failed to delete: ${extractMsg(error)}`, 'error'); }
  };

  const addTestimonial = async (data: NewTestimonialData) => {
      try {
          const newT = await backend.testimonials.add(data);
          setTestimonials([...testimonials, newT]);
          addNotification('Review submitted', 'success');
      } catch (error) { addNotification(`Failed: ${extractMsg(error)}`, 'error'); }
  };

  const submitInquiry = async (data: NewInquiryData) => {
      try {
          await backend.inquiries.add(data);
          addNotification('Inquiry sent! We will contact you soon.', 'success');
      } catch (error) { addNotification(`Failed to send: ${extractMsg(error)}`, 'error'); }
  };

  const updateInquiryStatus = async (id: number, status: 'Read' | 'Replied') => {
      try {
          await backend.inquiries.updateStatus(id, status);
          setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      } catch (error) { addNotification(`Failed: ${extractMsg(error)}`, 'error'); }
  };

  const updateSiteContent = async (key: string, value: any) => {
      try {
          await backend.cms.update(key, value);
          setSiteContent(prev => ({ ...prev, [key]: value }));
          addNotification('Settings saved', 'success');
      } catch (error) { addNotification(`Failed to save: ${extractMsg(error)}`, 'error'); }
  };

  const resetDatabase = async () => {
      try {
          await backend.resetDatabase();
          setProducts([]);
          setOrders([]);
          setInvoices([]);
          setInventoryUsageLogs([]);
          await refreshData();
          addNotification('Database reset (data cleared)', 'success');
      } catch (error) { addNotification(`Reset failed: ${extractMsg(error)}`, 'error'); }
  };

  return (
    <AppContext.Provider value={{
      currentUser,
      isAuthenticated,
      isLoading,
      products,
      categories,
      orders,
      invoices,
      customers,
      testimonials,
      notifications,
      customerInbox,
      adminInbox,
      inquiries,
      siteContent,
      orderEvents,
      shipments,
      inventoryItems,
      inventoryUsageLogs,
      branches,
      paymentTransactions,
      themeMode,
      resolvedTheme,
      login,
      signup,
      logout,
      forgotPassword,
      switchUser,
      addCategory,
      updateCategory,
      deleteCategory,
      addProduct,
      updateProduct,
      deleteProduct,
      createOrder,
      amendDeniedOrder,
      updateOrderStatus,
      reviewCustomerOrder,
      shipOrder,
      markShipmentDelivered,
      setOrderItemPacked,
      submitOrderReceiptChecks,
      syncInventoryFromReceipt,
      updateInventoryThreshold,
      issueInventoryStock,
      markInvoiceAsPaid,
      processInvoicePayment,
      requestInvoiceRefund,
      downloadPaymentReceipt,
      reconcileInvoice,
      updateInvoice,
      addCustomer,
      addAdmin,
      createCustomerOrgMember,
      addBranch,
      updateBranch,
      deleteBranch,
      updateBranchProductPrice,
      updateCustomer,
      updateCustomerRole,
      deleteCustomer,
      approveCustomer,
      rejectCustomer,
      addTestimonial,
      submitInquiry,
      updateInquiryStatus,
      updateSiteContent,
      addNotification,
      setThemeMode,
      cycleThemeMode,
      removeNotification,
      markInboxNotificationRead,
      markAllInboxNotificationsRead,
      markAdminNotificationRead,
      markAllAdminNotificationsRead,
      resetDatabase,
      setupRequired,
      isSetupMode,
      openSetup: () => setIsSetupMode(true),
      closeSetup: () => setIsSetupMode(false)
    }}>
      {children}
    </AppContext.Provider>
  );
};
